<template>
  <div v-if="isOpen" class="fixed inset-0 z-50">
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-30" @click="$emit('close')"></div>

    <!-- Panel -->
    <div class="fixed right-0 top-0 h-full w-[420px] max-w-[90vw] bg-white shadow-xl overflow-y-auto">
      <div class="p-6">
        <!-- Header -->
        <div class="flex justify-between items-center mb-6">
          <h2 class="text-lg font-semibold text-gray-900">Help Guide</h2>
          <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <!-- Sections -->
        <div class="space-y-1">

          <!-- Overview -->
          <div class="border border-gray-200 rounded-lg">
            <button @click="toggle('overview')" class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg">
              <span class="text-sm font-semibold text-gray-900">Overview</span>
              <svg class="w-4 h-4 text-gray-400 transition-transform" :class="{ 'rotate-180': open.overview }" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div v-if="open.overview" class="px-4 pb-4 text-sm text-gray-600 space-y-2">
              <p>Owen Hub automates project setup across four systems: <strong>Synergist</strong> (jobs, phases, stages), <strong>Basecamp</strong> (project management), <strong>Google Drive</strong> (file storage), and <strong>Google Spaces</strong> (team chat).</p>
              <p><strong>Typical workflow:</strong></p>
              <ol class="list-decimal pl-4 space-y-1">
                <li>Select a <strong>client</strong> from the dropdown in the nav bar</li>
                <li>Expand a <strong>job</strong> on the Dashboard to see its phases</li>
                <li>Click a <strong>phase</strong> to open the setup view</li>
                <li><strong>Sync platforms</strong> &mdash; create or link Basecamp projects, Drive folders, and Spaces</li>
                <li>Manage <strong>deliverables</strong> &mdash; sync Basecamp to-dos and Drive folders for each stage</li>
              </ol>
              <p>Each phase only needs to be set up once. After that, the platforms stay linked and the deliverables table tracks status.</p>
            </div>
          </div>

          <!-- Jobs & Phases -->
          <div class="border border-gray-200 rounded-lg">
            <button @click="toggle('jobs')" class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg">
              <span class="text-sm font-semibold text-gray-900">Jobs &amp; Phases</span>
              <svg class="w-4 h-4 text-gray-400 transition-transform" :class="{ 'rotate-180': open.jobs }" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div v-if="open.jobs" class="px-4 pb-4 text-sm text-gray-600 space-y-2">
              <p>This is your starting point. Jobs and phases are pulled live from Synergist.</p>
              <p>Select a <strong>client</strong> from the dropdown above to filter, then expand a job to see its phases. Click a phase to open the setup view where you can connect Basecamp, Drive, and Spaces.</p>
              <p>Use <strong>Search</strong> to find jobs by number or description across all clients.</p>
            </div>
          </div>

          <!-- Client Settings -->
          <div class="border border-gray-200 rounded-lg">
            <button @click="toggle('clientSettings')" class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg">
              <span class="text-sm font-semibold text-gray-900">Client Settings</span>
              <svg class="w-4 h-4 text-gray-400 transition-transform" :class="{ 'rotate-180': open.clientSettings }" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div v-if="open.clientSettings" class="px-4 pb-4 text-sm text-gray-600 space-y-2">
              <p>Each client needs a Basecamp account and a Google Drive folder linked before you can set up phases.</p>
              <p><strong>Basecamp account</strong> &mdash; which Basecamp organisation this client's projects live in. Set once per client.</p>
              <p><strong>Drive folder</strong> &mdash; the client's root folder on the Shared Drive. All phase folders are created inside it.</p>
              <p>These are configured once and apply to all phases for this client.</p>
            </div>
          </div>

          <!-- Platform Setup -->
          <div class="border border-gray-200 rounded-lg">
            <button @click="toggle('platform')" class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg">
              <span class="text-sm font-semibold text-gray-900">Platform Setup</span>
              <svg class="w-4 h-4 text-gray-400 transition-transform" :class="{ 'rotate-180': open.platform }" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div v-if="open.platform" class="px-4 pb-4 text-sm text-gray-600 space-y-2">
              <p>This section checks whether Basecamp, Google Drive, and Google Spaces are already set up for this phase.</p>
              <div class="space-y-1">
                <p><span class="inline-block w-2 h-2 rounded-full bg-green-500 mr-1"></span><strong>Green</strong> = connected and ready</p>
                <p><span class="inline-block w-2 h-2 rounded-full bg-amber-500 mr-1"></span><strong>Amber</strong> = found but not linked, or needs attention</p>
                <p><span class="inline-block w-2 h-2 rounded-full bg-red-400 mr-1"></span><strong>Red</strong> = not found &mdash; will be created when you sync</p>
              </div>
              <p>Click to expand and configure each platform, then hit <strong>Sync</strong> to create or link them.</p>
            </div>
          </div>

          <!-- What does Sync do? -->
          <div class="border border-gray-200 rounded-lg">
            <button @click="toggle('sync')" class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg">
              <span class="text-sm font-semibold text-gray-900">What does Sync do?</span>
              <svg class="w-4 h-4 text-gray-400 transition-transform" :class="{ 'rotate-180': open.sync }" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div v-if="open.sync" class="px-4 pb-4 text-sm text-gray-600 space-y-2">
              <p>Sync creates or links the selected platforms for this phase:</p>
              <ul class="list-disc pl-4 space-y-0.5">
                <li><strong>Basecamp</strong> &mdash; creates a project (from template if selected) or links an existing one</li>
                <li><strong>Google Drive</strong> &mdash; creates the phase folder inside the client's Shared Drive</li>
                <li><strong>Google Spaces</strong> &mdash; creates a Google Chat space for the project team</li>
              </ul>
              <p>Only ticked platforms are affected. Already-connected platforms are skipped.</p>
            </div>
          </div>

          <!-- Deliverables (Stages) -->
          <div class="border border-gray-200 rounded-lg">
            <button @click="toggle('deliverables')" class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg">
              <span class="text-sm font-semibold text-gray-900">Deliverables (Stages)</span>
              <svg class="w-4 h-4 text-gray-400 transition-transform" :class="{ 'rotate-180': open.deliverables }" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div v-if="open.deliverables" class="px-4 pb-4 text-sm text-gray-600 space-y-2">
              <p>Each row is a Synergist stage (deliverable). The platform columns show whether a matching Basecamp to-do and Drive folder exist.</p>
              <p class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide !mb-1">Platform column icons</p>
              <div class="space-y-1.5 text-xs">
                <div class="flex items-center gap-2"><span class="w-2.5 h-2.5 rounded-full bg-green-500 flex-shrink-0"></span> <span><strong>Green dot</strong> &mdash; linked &amp; completed (BC) or folder exists (Drive)</span></div>
                <div class="flex items-center gap-2"><span class="w-2.5 h-2.5 rounded-full bg-blue-500 flex-shrink-0"></span> <span><strong>Blue dot</strong> &mdash; linked &amp; active (BC to-do not yet completed)</span></div>
                <div class="flex items-center gap-2"><input type="checkbox" checked disabled class="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 flex-shrink-0" /> <span><strong>Ticked checkbox</strong> &mdash; will be created on next sync</span></div>
                <div class="flex items-center gap-2"><input type="checkbox" disabled class="h-3.5 w-3.5 rounded border-gray-300 flex-shrink-0" /> <span><strong>Empty checkbox</strong> &mdash; skipped (untick to exclude from sync)</span></div>
                <div class="flex items-center gap-2"><span class="text-gray-300 font-medium flex-shrink-0">&mdash;</span> <span><strong>Dash</strong> &mdash; platform not connected</span></div>
              </div>
              <p class="text-[11px] font-semibold text-gray-500 uppercase tracking-wide !mb-1">Stage badge</p>
              <div class="space-y-1.5 text-xs">
                <div class="flex items-center gap-2"><span class="w-4 h-4 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0"><svg class="h-2.5 w-2.5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg></span> <span><strong>Green tick</strong> &mdash; stage marked as finished</span></div>
                <div class="flex items-center gap-2"><span class="w-4 h-4 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0 text-[9px] font-semibold text-indigo-700">01</span> <span><strong>Number</strong> &mdash; stage in progress</span></div>
              </div>
              <p>Use <strong>Batch update</strong> to change status across multiple stages at once. Hover any icon for more detail.</p>
            </div>
          </div>

          <!-- Unmatched Basecamp Tasks -->
          <div class="border border-gray-200 rounded-lg">
            <button @click="toggle('unmatched')" class="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 rounded-lg">
              <span class="text-sm font-semibold text-gray-900">Unmatched Basecamp Tasks</span>
              <svg class="w-4 h-4 text-gray-400 transition-transform" :class="{ 'rotate-180': open.unmatched }" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" /></svg>
            </button>
            <div v-if="open.unmatched" class="px-4 pb-4 text-sm text-gray-600 space-y-2">
              <p>These are Basecamp to-dos that don't yet have a matching Synergist stage. This happens when tasks were added directly in Basecamp.</p>
              <p>Use the dropdown to <strong>link</strong> each task to an existing Synergist stage, then tick and click <strong>Import</strong> to save the mapping.</p>
              <p class="text-gray-400">Creating new Synergist stages from here is not yet available (pending Synergist API fix).</p>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { reactive } from 'vue'

export default {
  name: 'HelpPanel',
  props: {
    isOpen: { type: Boolean, default: false }
  },
  emits: ['close'],
  setup() {
    const open = reactive({
      overview: true,
      jobs: false,
      clientSettings: false,
      platform: false,
      sync: false,
      deliverables: false,
      unmatched: false
    })

    const toggle = (key) => { open[key] = !open[key] }

    return { open, toggle }
  }
}
</script>
